import { ChangeDetectorRef, Component } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { GenericDialogComponent } from "../../shared/generic-dialog/generic-dialog.component";
import { CartItemService } from "src/app/services/cart-item/cart-item.service";
import { Router } from "@angular/router";
import { CartDetails, ProductCategories, ProductAttributes } from "@models/product.model";
import { LocalStorageService } from "../../services/local-storage/local-storage.service";
import { debounceTime, finalize, forkJoin, Subject, switchMap, switchMapTo, timer } from "rxjs";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { appConfig } from "../../../environments/app.config";
import { ProductService } from "../../services/product/product.service";
import { TranslateService } from "@ngx-translate/core";
import { BaseCart, CartAttributes, RemoveCartItem } from "@models/cart.model";
import { WishlistService } from "../../services/wishlistService/wishlist.service";
import { WishListAttributes } from "@models/wishlist.model";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Location } from "@angular/common";
import { AuthService } from "../../services/auth/auth.service";
import { DialogService } from "../../services/dialog/dialog.service";
import { ColorsPicker } from "../../../utils/color-constants";
import { environment } from "~/src/environments/environment";
import { CouponService } from "../../services/coupon/coupon.service";
import { CouponAttributes } from "@models/coupon.model";
import { UserService } from "../../services/user/user.service";
import { NullableAddress, NullableRegisteredUserDetails, NullableUser } from "@models/user.model";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { CheckoutService } from "../../services/checkout-page/checkout.service";

@Component({
    selector: "app-cart-page",
    templateUrl: "./cart-page.component.html",
    styleUrls: ["./cart-page.component.scss"],
    standalone: false,
})
export class CartPageComponent {
    public isLoggedInUser = false;
    cartItems: CartAttributes[] = [];
    products: CartDetails[] = [];
    cartId!: number;
    totalMRP!: number;
    totalDiscount!: number;
    totalAmount!: number;
    public isCheckoutEnabled: boolean = false;
    public minOrderValue = appConfig.minOrderValue;
    public loading: boolean = false;
    public categories = ProductCategories;
    public wishlistSkipValue: number = 0;
    public wishlistData: ProductAttributes[] = [];
    public currentStep: number = 1;
    public deliveryCharge: number = 0;
    private showMoveToWishlistButton: boolean = false; // when user is logged in only then show move to wishlist button
    private cartUpdateSubject = new Subject<{ productId: number; quantity: number; cartUUID: string }>();
    public freeDeliveryAmount = 200;
    public isHavingAddress: boolean = true;
    public availableCoupons: CouponAttributes[] = [];
    public userDetails: NullableRegisteredUserDetails = null;
    public isCouponApplied: boolean = false;
    public selectedCartItems = new Map<string, RemoveCartItem>();
    public currentUserAddress?: NullableAddress;
    public currentUser?: NullableUser;

    constructor(
        private dialog: MatDialog,
        private cartItemService: CartItemService,
        private router: Router,
        private localStorageService: LocalStorageService,
        private cartService: CartItemService,
        private cdr: ChangeDetectorRef,
        private productService: ProductService,
        private translate: TranslateService,
        private wishlistService: WishlistService,
        private snackBar: MatSnackBar,
        private location: Location,
        public authService: AuthService,
        private dialogService: DialogService,
        private couponsService: CouponService,
        private userService: UserService,
        private checkoutService: CheckoutService,
    ) {}

    ngOnInit(): void {
        if (this.authService.isUserLoggedIn) {
            this.showMoveToWishlistButton = true;
            this.getRegisteredUserDetails();
        } else {
            this.getGuestUserDetails();
        }
        this.loadCartItems();
        this.cartUpdateSubject
            .pipe(
                debounceTime(250), // Adjust the time as needed
            )
            .subscribe(({ productId, quantity, cartUUID }) => {
                this.updateCartQuantity(productId, quantity, cartUUID);
            });
        this.getsuggestedProducts();
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private getRegisteredUserDetails(): void {
        this.userService.getUserDetails().subscribe((response) => {
            if (response) {
                this.userDetails = response;
                this.currentUser = response.user;
                this.currentUserAddress = response.addresses?.find((one) => one.isPreferred);
                this.fetchAvailableCoupons(this.userDetails?.mobileNumber!);
            }
        });
    }

    private getGuestUserDetails(): void {
        this.currentUserAddress = this.checkoutService.getAddressFromLocalStorage();
        this.currentUser = this.checkoutService.getCurrentUserFromLocalStorage();
    }

    private loadCartItems(): void {
        this.loading = true;
        timer(100)
            .pipe(
                switchMap(() => this.cartItemService.getCartItems()), // Fetch cart items after delay
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe({
                next: (items) => {
                    this.cartItems = items;
                    const updatedItemsInCart = this.cartItems.map(this.cartItemService.mapToCartItem); // To get the latest item from local ae well as db with cross browser
                    if (updatedItemsInCart.length) {
                        this.localStorageService.setCartItems(updatedItemsInCart);
                    } else {
                        this.localStorageService.removeAllCartItems();
                    }
                    this.products = this.cartItems.map((item) => ({
                        product: item.product!,
                        singleBlockInfo: item.singleBlockInfo!,
                        cartUUID: item.cartUUID,
                        cartId: item.id,
                    }));

                    this.calculateTotalValues();
                    const inActiveIds = this.products
                        .filter((product) => !product.product?.isActive)
                        .map((item) => ({
                            id: item.product?.id,
                            cartUUID: item.cartUUID,
                        }));

                    if (inActiveIds.length) {
                        this.openDialogUnavailable(inActiveIds);
                    }
                    if (this.authService.isUserLoggedIn) {
                        this.loadMyWishlistItems();
                    }
                },
                error: (error) => {
                    throw error;
                },
            });
    }

    private loadMyWishlistItems(): void {
        this.wishlistService.getWishListItems(this.wishlistSkipValue).subscribe({
            next: (response) => {
                this.wishlistData = response.rows.map((item: WishListAttributes) => item.product!);
            },
            error: (error) => {
                throw error;
            },
        });
    }

    private openDialogUnavailable(inActiveIds: { id: number; cartUUID?: string }[]): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.UnavailableProducts,
                title: this.getTranslation("genericDialogBox.unavailableProductCart.title"),
                content: this.getTranslation("genericDialogBox.unavailableProductCart.content"),
                firstBtn: this.getTranslation("genericDialogBox.button.removeBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                const removeObservables = inActiveIds.map(({ id, cartUUID }) => {
                    const items: RemoveCartItem = { productId: id };
                    if (cartUUID) {
                        items.cartUUID = cartUUID;
                    }
                    return this.cartItemService.removeCartItem([items]);
                });

                forkJoin(removeObservables).subscribe(() => {
                    this.loadCartItems();
                });
            }
        });
    }

    private calculateTotalValues(): void {
        this.cartItemService.calculateTotalMRP(this.cartItems.filter((item) => item.product?.isActive));
        this.cdr.detectChanges();
        this.totalMRP = this.cartItemService.totalMRP;
        this.totalDiscount = this.cartItemService.totalDiscount;
        this.totalAmount = this.cartItemService.subTotal;
        this.isCheckoutEnabled = this.totalAmount >= appConfig.minOrderValue;
    }

    public onDeleteCartItem(item: CartDetails): void {
        this.openDialog(item);
    }

    private openDialog(item: CartDetails): void {
        const hasSecondButton = !item.singleBlockInfo && this.showMoveToWishlistButton;
        const dialogData = {
            componentName: DialogInvokingComponents.RemoveFromCart,
            title: this.getTranslation("genericDialogBox.removeCart.title"),
            image: item.product?.images?.imagesUrl[0],
            content: this.getTranslation("genericDialogBox.removeCart.content"),
            firstBtn: this.getTranslation("genericDialogBox.button.removeBtn"),
            // only add secondBtn if allowed
            ...(hasSecondButton && {
                secondBtn: this.getTranslation("genericDialogBox.button.moveToWishlist"),
            }),
        };
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: dialogData,
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === "move") {
                this.moveToWishlist(item.product);
            } else {
                if (result) this.removeProductFromCart(item.product?.id!, item.cartUUID!);
            }
        });
    }

    public onCheckout(): void {
        if (!this.authService.isUserLoggedIn) {
            this.loginDialog();
        } else {
            this.router.navigate(["address"]);
        }
    }

    public onNavigateToProductPage(): void {
        this.router.navigate(["/products"]);
    }

    public loginDialog(): void {
        const dialogData = {
            componentName: DialogInvokingComponents.LoginDialog,
        };
        // small dialogbox for mobile
        const isSmallScreen = window.innerWidth <= 550;
        this.dialogService
            .openDialog(dialogData, "90%", { disableClose: false, maxHeight: isSmallScreen ? "75vh" : "none" }, "430px")
            .afterClosed()
            .subscribe((response) => {
                this.router.navigate(["address"]);
            });
    }

    public backToHome(): void {
        this.router.navigate(["home"]);
    }

    public getProductQtyInCart(productId: number, cartUUID: string): number {
        return this.localStorageService.getCartedQuantity(productId, cartUUID);
    }

    public onClick(value: number, productId: number, cartUUID: string): void {
        const quantityInCart = this.getProductQtyInCart(productId, cartUUID);
        const quantity = quantityInCart + value;
        this.cartUpdateSubject.next({ productId, quantity, cartUUID });
    }

    private updateCartQuantity(productId: number, quantity: number, cartUUID: string): void {
        if (quantity < 1) {
            this.removeProductFromCart(productId, cartUUID);
        } else if (quantity <= 99) {
            this.updateProductQuantityInCart(productId, quantity, cartUUID);
        }
    }

    private removeProductFromCart(id: number, cartUUID?: string): void {
        const items: RemoveCartItem = { productId: id };
        if (cartUUID) {
            items.cartUUID = cartUUID;
        }

        this.cartService.removeCartItem([items]).subscribe((response) => {
            if (response) {
                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);

                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
                const next = new Map(this.selectedCartItems);
                [items].forEach((item) => {
                    next.delete(this.buildKey(item.productId!, item.cartUUID));
                });
                this.selectedCartItems = next;
                this.loadCartItems();
            }
        });
    }

    private updateProductQuantityInCart(productId: number, quantity: number, cartUUID: string): void {
        this.cartService.updateCartQuantity(productId, quantity, cartUUID).subscribe(() => {
            const item = this.cartItems.find((item) => item.product?.id === productId && item.cartUUID === cartUUID);
            if (item) {
                item.quantity = quantity;
                this.calculateTotalValues();
            }
        });
    }

    public onInputChange(event: Event, productId: number, cartUUID: string): void {
        let inputValue = (event.target as HTMLInputElement).value.trim();
        let quantity = parseInt(inputValue, 10);

        if (isNaN(quantity)) {
            return;
        } else if (quantity < 1) {
            (event.target as HTMLInputElement).value = "1";
            quantity = parseInt((event.target as HTMLInputElement).value, 10);
        } else if (inputValue.length > 2) {
            inputValue = inputValue.slice(0, 2);
            (event.target as HTMLInputElement).value = inputValue;
            return;
        }

        this.cartUpdateSubject.next({ productId, quantity, cartUUID });
    }

    public onBlur(event: Event, productId: number, cartUUID: string): void {
        const inputValue = (event.target as HTMLInputElement).value.trim();
        if (!inputValue) {
            (event.target as HTMLInputElement).value = "1";
            const quantity = parseInt((event.target as HTMLInputElement).value, 10);
            this.cartUpdateSubject.next({ productId, quantity, cartUUID });
        }
    }

    public onClickProduct(productId: number): void {
        this.router.navigate([`products/${productId}`]);
    }

    public getFacesByType(data: CartDetails, type: string): string[] {
        return this.cartItemService.getFacesByType(data, type);
    }

    public getFaceColors(colorObject: object | undefined): { key: string; value: string }[] {
        if (!colorObject) {
            return [];
        }
        return Object.entries(colorObject).map(([key, value]) => ({
            key,
            value: value as string, // Ensure the value is treated as a string
        }));
    }

    public getdiscountPercentage(product: ProductAttributes): number {
        return Math.round(((product.price - product?.discountedPrice!) / product.price) * 100);
    }

    public moveToWishlist(product: ProductAttributes): void {
        const productId = product.id;

        const finalizeMove = () => {
            this.removeProductFromCart(productId);
            this.loadCartItems();
            this.loadMyWishlistItems();
        };

        if (this.wishlistService.getAllwishlistIDs.productIDs.includes(productId)) {
            finalizeMove();
        } else {
            this.wishlistService.addToWishList([productId]).subscribe(finalizeMove);
        }
    }

    public goBack(): void {
        this.location.back();
    }

    public clickImage(productId: number): void {
        this.router.navigate([`products/${productId}`]);
    }

    public isCategoryBuyBlocks(category: string[] | undefined): boolean {
        return Array.isArray(category) && category.includes(ProductCategories.BuyBlocks);
    }

    public goTohomePage(): void {
        this.router.navigate([`home`]);
    }

    public getColorValue(color: string): string | undefined {
        return ColorsPicker.find((c) => c.name === color)?.value;
    }

    private openSnackBar(message: string, action: string): void {
        this.snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    public suggestedProducts: ProductAttributes[] = [];

    private getsuggestedProducts() {
        this.productService
            .getAllActiveProducts(environment.dynamicPagination.pageIndex, 4, "", ProductCategories.BestSeller)
            .subscribe((response) => {
                this.suggestedProducts = response.rows;
            });
    }

    public fetchAvailableCoupons(mobileNumber: string): void {
        this.couponsService.getUserCoupons().subscribe({
            next: (response) => {
                this.availableCoupons = response.filter((data) => {
                    return (
                        !data.isUsed?.userIds.includes(mobileNumber) &&
                        Object.values(data.discountType).find((type) => type === "Users")
                    );
                });
            },
        });
    }

    public toogleFavorite(product: ProductAttributes): void {
        if (this.authService.isUserLoggedIn) {
            if (this.isInWishlist(product)) {
                this.wishlistService.deleteFromMyWishlist(product.id).subscribe();
            } else {
                this.wishlistService.addToWishList([product.id]).subscribe();
            }
        } else {
            this.dialogService.loginDialog();
        }
    }

    public isInWishlist(product: ProductAttributes): boolean {
        if (this.authService.isUserLoggedIn) {
            return this.wishlistService.getAllwishlistIDs?.productIDs.includes(product.id);
        } else {
            return false;
        }
    }

    public buildKey(productId: number, cartUUID?: string): string {
        return `${productId}|${cartUUID ?? ""}`;
    }

    public isItemSelected(item: CartDetails): boolean {
        return this.selectedCartItems.has(this.buildKey(item.product.id, item.cartUUID));
    }

    public toggleSelectCart(item: CartDetails): void {
        const key = this.buildKey(item.product.id, item.cartUUID);

        if (this.selectedCartItems.has(key)) {
            this.selectedCartItems.delete(key);
        } else {
            this.selectedCartItems.set(key, {
                productId: item.product.id,
                cartUUID: item.cartUUID,
            });
        }
    }

    public selectAllCarts(): void {
        this.products.forEach((item) => {
            const key = this.buildKey(item.product.id, item.cartUUID);
            this.selectedCartItems.set(key, {
                productId: item.product.id,
                cartUUID: item.cartUUID,
            });
        });
    }

    public deselectAllCarts(): void {
        this.selectedCartItems.clear();
    }

    public isAllSelected(): boolean {
        return (
            this.products.length > 0 &&
            this.products.every((item) => this.selectedCartItems.has(this.buildKey(item.product.id, item.cartUUID)))
        );
    }

    public isSomeSelected(): boolean {
        return this.selectedCartItems.size > 0 && !this.isAllSelected();
    }

    public toggleSelectAll(event: MatCheckboxChange): void {
        event.checked ? this.selectAllCarts() : this.deselectAllCarts();
    }

    public onClickMultipleCarts(action: string): void {
        if (this.selectedCartItems.size > 0) {
            this.openDialogForMultipleCarts();
        } else {
            this.openSnackBar(
                action === "remove" ? "Select any item to remove from bag" : "Select any item to move to wishlist",
                SnackBarConfig.Actions.OKAY,
            );
        }
    }

    public openDialogForMultipleCarts(): void {
        const dialogData = {
            componentName: DialogInvokingComponents.RemoveFromCart,
            title: `Move ${this.selectedCartItems.size} Item(s)`,
            content: `Are you sure you want to move these ${this.selectedCartItems.size} items from your cart?`,
            firstBtn: "Remove",
            secondBtn: "Move to Wishlist",
        };
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: dialogData,
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === "move") {
                this.moveMultipleCartstoWishlist();
            } else {
                if (result) this.removeMultipleCarts();
            }
        });
    }

    public removeMultipleCarts(): void {
        const items = Array.from(this.selectedCartItems.values());
        this.cartItemService.removeCartItem(items).subscribe((response) => {
            if (response) {
                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
                const next = new Map(this.selectedCartItems);
                items.forEach((item) => {
                    next.delete(this.buildKey(item.productId!, item.cartUUID));
                });
                this.selectedCartItems = next;
                this.loadCartItems();
            }
        });
    }

    public moveMultipleCartstoWishlist(): void {
        const items = Array.from(this.selectedCartItems.values());
        const productIds = items.map((item) => item.productId).filter((id): id is number => id !== undefined);

        const existing = this.wishlistService.getAllwishlistIDs.productIDs;
        const toAdd = productIds.filter((id) => !existing.includes(id!));

        const finalizeMove = () => {
            this.removeMultipleCarts();
            this.loadMyWishlistItems();
        };

        if (!toAdd.length) {
            finalizeMove();
        } else {
            this.wishlistService.addToWishList(toAdd).subscribe(finalizeMove);
        }
    }
}
